/**
 * Item tag and `nr` generation for duplicating inventory items and creating
 * several at once.
 *
 * Both identifiers are unique per product type in the database
 * (inventory_items_product_type_id_item_number_key and
 * inventory_items_product_type_id_nr_key), so every generator here takes the
 * identifiers already in use for that product type and skips them. The caller
 * passes a fresh list; nothing here reads the database.
 */

/** Trailing digit run of a tag: "BIKE-007" -> { prefix: "BIKE-", digits: "007" }. */
function splitTrailingNumber(tag: string): { prefix: string; digits: string } | null {
  const match = /^(.*?)(\d+)$/.exec(tag);
  if (!match) return null;
  return { prefix: match[1], digits: match[2] };
}

/**
 * The tag that follows `tag` in its own numbering: the trailing number is
 * incremented and the zero padding kept ("BIKE-007" -> "BIKE-008",
 * "BIKE-099" -> "BIKE-100"). A tag with no trailing number gets "-2".
 */
export function incrementItemNumber(tag: string): string {
  const parts = splitTrailingNumber(tag);
  if (!parts) return `${tag}-2`;
  const next = String(BigInt(parts.digits) + 1n);
  return parts.prefix + next.padStart(parts.digits.length, '0');
}

/**
 * The first tag after `tag` in its numbering that is not in `taken`.
 * Used to suggest the tag of a duplicate.
 */
export function nextFreeItemNumber(tag: string, taken: Iterable<string>): string {
  const used = new Set(Array.from(taken, (t) => t.trim().toLowerCase()));
  let candidate = incrementItemNumber(tag.trim());
  // Bounded so a pathological `taken` set can never spin forever.
  for (let i = 0; i < 100000 && used.has(candidate.toLowerCase()); i++) {
    candidate = incrementItemNumber(candidate);
  }
  return candidate;
}

/**
 * `count` tags starting from `first`, each one the increment of the one
 * before: "BIKE-001" x3 -> BIKE-001, BIKE-002, BIKE-003. A tag without a
 * trailing number is suffixed instead: "BIKE" x3 -> BIKE-1, BIKE-2, BIKE-3.
 *
 * Tags already in `taken` are skipped, so a run that crosses an existing item
 * continues past it. The first tag is never skipped: the user typed it, so a
 * clash there is reported by {@link findItemNumberConflicts} instead.
 */
export function generateItemNumbers(first: string, count: number, taken: Iterable<string> = []): string[] {
  const base = first.trim();
  if (count <= 1 || !base) return [base];

  const used = new Set(Array.from(taken, (t) => t.trim().toLowerCase()));
  const result: string[] = [];

  if (!splitTrailingNumber(base)) {
    let n = 1;
    while (result.length < count) {
      const candidate = `${base}-${n++}`;
      if (!used.has(candidate.toLowerCase())) result.push(candidate);
    }
    return result;
  }

  result.push(base);
  let candidate = base;
  while (result.length < count) {
    candidate = incrementItemNumber(candidate);
    if (!used.has(candidate.toLowerCase())) result.push(candidate);
  }
  return result;
}

/** Tags in `tags` that are already in `taken` (case-insensitive) or repeat within `tags`. */
export function findItemNumberConflicts(tags: string[], taken: Iterable<string>): string[] {
  const used = new Set(Array.from(taken, (t) => t.trim().toLowerCase()));
  const seen = new Set<string>();
  const conflicts: string[] = [];
  for (const tag of tags) {
    const key = tag.trim().toLowerCase();
    if (used.has(key) || seen.has(key)) conflicts.push(tag);
    seen.add(key);
  }
  return conflicts;
}

/**
 * `count` values for the NOT NULL `nr` column, none of them in `taken`.
 * Counting starts at `start` when given, otherwise right after the highest
 * number already in use (1 for an empty product type).
 */
export function allocateNrs(start: number | null, count: number, taken: Iterable<number>): number[] {
  const used = new Set<number>();
  let max = 0;
  for (const n of taken) {
    used.add(n);
    if (n > max) max = n;
  }
  let candidate = start != null && start > 0 ? start : max + 1;
  const result: number[] = [];
  while (result.length < count) {
    if (!used.has(candidate)) result.push(candidate);
    candidate++;
  }
  return result;
}
